import { useEffect, useState, useRef } from "react";
import { formatNumber, parseUSD } from "../utils/currency";

/**
 * Currency input with US-standard masking.
 * - Displays as the user types; auto-formats with commas on blur.
 * - Emits a plain Number (not formatted string) via onChange.
 * - Forces 2 decimal places on blur.
 *
 * Props:
 *   value: number  — the canonical numeric value held by the parent.
 *   onChange: (num: number) => void
 *   prefix: bool   — show "$" prefix inside the input (default true)
 *   className, placeholder, disabled, "data-testid", min, max
 */
export default function CurrencyInput({
  value,
  onChange,
  prefix = true,
  className = "",
  placeholder = "0.00",
  disabled = false,
  min,
  max,
  ...rest
}) {
  // Local display string so the user can type intermediate values like "1," or "12."
  const [display, setDisplay] = useState(() => {
    const n = Number(value);
    return Number.isFinite(n) && n !== 0 ? formatNumber(n) : "";
  });
  const focused = useRef(false);

  // Sync from parent when value changes externally (not from this input)
  useEffect(() => {
    if (focused.current) return;
    const n = Number(value);
    setDisplay(Number.isFinite(n) && n !== 0 ? formatNumber(n) : "");
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    // Allow only digits, commas, dot, leading minus
    const cleaned = raw.replace(/[^0-9.,\-]/g, "");
    setDisplay(cleaned);
    const num = parseUSD(cleaned);
    let clamped = num;
    if (typeof min === "number" && clamped < min) clamped = min;
    if (typeof max === "number" && clamped > max) clamped = max;
    onChange(clamped);
  };

  const handleFocus = () => {
    focused.current = true;
    // On focus, show raw number (no commas) so editing is easier
    const n = Number(value);
    setDisplay(Number.isFinite(n) && n !== 0 ? String(n) : "");
  };

  const handleBlur = () => {
    focused.current = false;
    const n = parseUSD(display);
    setDisplay(n === 0 && display.trim() === "" ? "" : formatNumber(n));
    if (n !== Number(value)) onChange(n);
  };

  return (
    <div className={`relative ${className.includes("w-") ? "" : "inline-block"}`}>
      {prefix && (
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-sm pointer-events-none select-none">
          $
        </span>
      )}
      <input
        {...rest}
        type="text"
        inputMode="decimal"
        disabled={disabled}
        value={display}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`${className} ${prefix ? "pl-5" : ""}`}
      />
    </div>
  );
}
